import { useEffect, useState, useRef } from 'react';
import { useThemeClasses } from '@/hooks/useTheme';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { Calendar, MapPin, Link as LinkIcon, Edit2, MessageSquare, Loader2, DoorOpen, Flag, MoreHorizontal, Ban, VolumeX, Volume2, UserX } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useUsers } from '@/hooks/useUsers';
import { usePosts } from '@/hooks/usePosts';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PostCard } from '@/components/PostCard';
import { EditProfileModal } from '@/components/EditProfileModal';
import { ReportModal } from '@/components/ReportModal';
import { ProfileConnections } from '@/components/community';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { BADGES } from '@/lib/badges';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, updateUser } = useAuth();
  const { fetchProfile, followUser, unfollowUser, checkFollowing, loading: userLoading } = useUsers();
  const { posts, fetchUserPosts, loading: postsLoading } = usePosts();
  const { isDark, textClass, textMutedClass, textVeryMutedClass, borderClass, hoverBgClass } = useThemeClasses();
  
  const [profile, setProfile] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [whisperLoading, setWhisperLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const isOwnProfile = currentUser?.username === username;

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url || res.data?.secure_url;
      if (url) {
        await axios.put(`${API}/users/profile`, { cover_url: url }, { withCredentials: true });
        setProfile(prev => ({ ...prev, cover_url: url }));
        toast.success('Cover updated');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setCoverUploading(false);
    }
  };

  const startWhisper = async () => {
    if (!profile || whisperLoading) return;
    
    setWhisperLoading(true);
    try {
      const response = await axios.post(
        `${API}/sidebar/create?other_user_id=${profile.user_id}`,
        {},
        { withCredentials: true }
      );
      toast.success('Starting sidebar...');
      navigate(`/sidebar/${response.data.sidebar_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sidebar');
    } finally {
      setWhisperLoading(false);
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      setProfileError(null);
      try {
        const userData = await fetchProfile(username);
        setProfile(userData);

        if (!isOwnProfile && currentUser) {
          const following = await checkFollowing(userData.user_id);
          setIsFollowing(following);
        }

        await fetchUserPosts(username);
      } catch (error) {
        const status = error.response?.status;
        if (status === 404) {
          // User genuinely doesn't exist — go home
          toast.error('User not found');
          navigate('/home');
        } else {
          // Network/auth/server error — show error state instead of bouncing
          setProfileError(error.response?.data?.detail || 'Failed to load profile');
        }
      }
    };

    loadProfile();
  }, [username, fetchProfile, fetchUserPosts, checkFollowing, isOwnProfile, currentUser, navigate]);

  const handleFollow = async () => {
    if (!profile || followLoading) return;
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollowUser(profile.user_id);
        setIsFollowing(false);
        setProfile(prev => ({
          ...prev,
          followers_count: prev.followers_count - 1
        }));
      } else {
        await followUser(profile.user_id);
        setIsFollowing(true);
        setProfile(prev => ({
          ...prev,
          followers_count: prev.followers_count + 1
        }));
      }
    } catch (error) {
      toast.error('Action failed. Try again.');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleProfileUpdate = (updatedProfile) => {
    setProfile(updatedProfile);
    if (isOwnProfile) {
      updateUser(updatedProfile);
    }
  };

  const handleBlock = async () => {
    if (!profile || blockLoading) return;
    
    setBlockLoading(true);
    try {
      if (isBlocked) {
        await axios.delete(`${API}/users/${profile.user_id}/block`, { withCredentials: true });
        setIsBlocked(false);
        toast.success(`Unblocked @${profile.username}`);
      } else {
        await axios.post(`${API}/users/${profile.user_id}/block`, {}, { withCredentials: true });
        setIsBlocked(true);
        setIsFollowing(false); // Blocking also unfollows
        toast.success(`Blocked @${profile.username}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setBlockLoading(false);
    }
  };

  const handleMute = async () => {
    if (!profile || muteLoading) return;
    
    setMuteLoading(true);
    try {
      if (isMuted) {
        await axios.delete(`${API}/users/${profile.user_id}/mute`, { withCredentials: true });
        setIsMuted(false);
        toast.success(`Unmuted @${profile.username}`);
      } else {
        await axios.post(`${API}/users/${profile.user_id}/mute`, {}, { withCredentials: true });
        setIsMuted(true);
        toast.success(`Muted @${profile.username}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setMuteLoading(false);
    }
  };

  const formatJoinDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM yyyy');
    } catch {
      return '';
    }
  };

  if (userLoading && !profile) {
    return (
      <div className="mb-safe" data-testid="profile-loading">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-8 text-center">
        <p className={textMutedClass}>Couldn't load this profile right now.</p>
        <button
          onClick={() => navigate(-1)}
          className={cn("px-4 py-2 text-sm border", borderClass, textMutedClass)}
        >
          Go back
        </button>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="mb-safe" data-testid="profile-page">
      {/* Profile Header */}
      <div className={cn("profile-header border-b", borderClass)}>
        {/* Cover photo — 160px */}
        <div
          style={{
            height: '160px',
            position: 'relative',
            overflow: 'hidden',
            background: profile.cover_url
              ? undefined
              : 'linear-gradient(160deg, #1a1100, #2C1E00)',
            backgroundImage: profile.cover_url ? `url(${profile.cover_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: isOwnProfile ? 'pointer' : 'default',
          }}
          onClick={isOwnProfile ? () => coverInputRef.current?.click() : undefined}
        >
          {/* Fallback watermark text */}
          {!profile.cover_url && (
            <>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 50%, rgba(240,123,29,0.12), transparent 60%)' }} />
              <div style={{ position: 'absolute', bottom: '8px', right: '14px', fontFamily: "'Bebas Neue', sans-serif", fontSize: '52px', color: 'rgba(255,215,0,0.04)', letterSpacing: '2px', lineHeight: 1, userSelect: 'none' }}>BLVX</div>
            </>
          )}
          {/* Dark overlay on hover for own profile */}
          {isOwnProfile && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .15s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
            >
              {coverUploading ? (
                <div style={{ width: '32px', height: '32px', border: '2px solid rgba(255,255,255,0.6)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '24px' }}>📷</span>
                  <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,0.8)' }}>CHANGE COVER</span>
                </div>
              )}
            </div>
          )}
          {/* Hidden file input */}
          {isOwnProfile && (
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverUpload}
            />
          )}
        </div>
        
        {/* Profile info */}
        <div className="px-4 pb-4">
          {/* Avatar row */}
          <div className="flex items-end justify-between -mt-12 mb-4">
            <Avatar className={cn("h-24 w-24 border-4", isDark ? "border-black" : "border-white")}>
              <AvatarImage src={profile.picture} alt={profile.name} />
              <AvatarFallback className={cn("text-2xl", isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-700")}>
                {profile.name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            {isOwnProfile ? (
              <Button
                variant="outline"
                className={cn("rounded-sm", isDark ? "border-white/30 text-white hover:bg-white hover:text-black" : "border-gray-300 text-gray-900 hover:bg-black hover:text-white")}
                onClick={() => setEditOpen(true)}
                data-testid="edit-profile-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/ai-stoop/${profile.username}`)}
                  className={cn("border rounded-sm", isDark ? "border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300" : "border-amber-500/50 text-amber-600 hover:bg-amber-50 hover:text-amber-700")}
                  data-testid="stoop-btn"
                  title="Leave them a message"
                >
                  <DoorOpen className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={startWhisper}
                  disabled={whisperLoading}
                  className={cn("border rounded-sm", isDark ? "border-white/20 text-white/70 hover:bg-white/10 hover:text-white" : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900")}
                  data-testid="whisper-btn"
                  title="Start a sidebar"
                >
                  {whisperLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={cn(
                    "rounded-sm px-6",
                    isFollowing
                      ? (isDark ? "bg-transparent border border-white/30 text-white hover:bg-white/10 hover:border-red-500 hover:text-red-500" : "bg-transparent border border-gray-300 text-gray-900 hover:bg-gray-100 hover:border-red-500 hover:text-red-500")
                      : (isDark ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90")
                  )}
                  data-testid="follow-btn"
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
                
                {/* More options dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("border rounded-sm", isDark ? "border-white/20 text-white/70 hover:bg-white/10 hover:text-white" : "border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900")}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card border-border">
                    <DropdownMenuItem 
                      onClick={handleMute}
                      disabled={muteLoading}
                      className={cn("cursor-pointer text-xs", isDark ? "text-white/70 hover:text-white focus:text-white" : "text-gray-600 hover:text-gray-900 focus:text-gray-900")}
                    >
                      {isMuted ? (
                        <>
                          <Volume2 className="h-3.5 w-3.5 mr-2" />
                          Unmute
                        </>
                      ) : (
                        <>
                          <VolumeX className="h-3.5 w-3.5 mr-2" />
                          Mute
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleBlock}
                      disabled={blockLoading}
                      className={cn("cursor-pointer text-xs", isBlocked ? "text-green-500 hover:text-green-400" : "text-red-500 hover:text-red-400")}
                    >
                      {isBlocked ? (
                        <>
                          <UserX className="h-3.5 w-3.5 mr-2" />
                          Unblock
                        </>
                      ) : (
                        <>
                          <Ban className="h-3.5 w-3.5 mr-2" />
                          Block
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setReportOpen(true)}
                      className={cn("cursor-pointer text-xs", isDark ? "text-white/70 hover:text-white focus:text-white" : "text-gray-600 hover:text-gray-900 focus:text-gray-900")}
                    >
                      <Flag className="h-3.5 w-3.5 mr-2" />
                      Report User
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Name and username */}
          <div className="mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className={cn("text-xl font-bold", textClass)}>{profile.name}</h1>
              {/* Crown — new member badge (joined within 7 days) */}
              {profile.created_at && (Date.now() - new Date(profile.created_at)) < 7 * 86400000 && (
                <span
                  title="Just entered the chat"
                  style={{
                    fontSize: '16px',
                    lineHeight: 1,
                    filter: 'drop-shadow(0 1px 4px rgba(240,123,29,0.9))',
                  }}
                >👑</span>
              )}
            </div>
            <p className={textMutedClass}>@{profile.username}</p>
          </div>

          {/* Active Badges */}
          {profile.active_badges?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {profile.active_badges.map((key) => {
                const badge = BADGES[key];
                if (!badge) return null;
                return (
                  <span
                    key={key}
                    className={cn("inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-sm", badge.bg, badge.color)}
                  >
                    {badge.emoji} {badge.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Bio */}
          {profile.bio && (
            <p className={cn("mb-3", isDark ? "text-white/80" : "text-gray-700")}>{profile.bio}</p>
          )}

          {/* Meta info */}
          <div className={cn("flex flex-wrap gap-4 text-sm mb-4", textMutedClass)}>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Joined {formatJoinDate(profile.created_at)}</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-6">
            <div className="text-sm">
              <span className={cn("font-semibold", textClass)}>{profile.following_count}</span>
              <span className={cn("ml-1", textMutedClass)}>Following</span>
            </div>
            <div className="text-sm">
              <span className={cn("font-semibold", textClass)}>{profile.followers_count}</span>
              <span className={cn("ml-1", textMutedClass)}>Followers</span>
            </div>
          </div>

          {/* Crown — Entered the Chat banner (new member, not own profile) */}
          {!isOwnProfile && profile.created_at && (Date.now() - new Date(profile.created_at)) < 3 * 86400000 && (
            <div style={{
              marginTop: '14px',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, rgba(240,123,29,0.1), rgba(28,34,48,0.6))',
              border: '1px solid rgba(240,123,29,0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ fontSize: '14px' }}>👑</span>
              <div>
                <p style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: '#F07B1D', textTransform: 'uppercase', margin: 0 }}>
                  Entered the Chat
                </p>
                <p style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px', color: '#72788A', margin: '2px 0 0' }}>
                  New to The Block — show some love
                </p>
              </div>
            </div>
          )}

          {/* Connection Info - mutuals, voucher, status */}
          {!isOwnProfile && profile.user_id && (
            <ProfileConnections userId={profile.user_id} className="mt-4" />
          )}
        </div>
      </div>

      {/* Posts Tab */}
      <div className={cn("border-b", borderClass)}>
        <div className="flex">
          <button className={cn("flex-1 py-4 text-sm font-medium relative", textClass)}>
            Posts
            <span className={cn("absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5", isDark ? "bg-white" : "bg-black")} />
          </button>
        </div>
      </div>

      {/* User's Posts */}
      {postsLoading && posts.length === 0 ? (
        <div className="p-4 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 px-6">
          <p className={cn("text-lg mb-2", textMutedClass)}>No posts yet</p>
          <p className={cn("text-sm", textVeryMutedClass)}>
            {isOwnProfile ? "You haven't posted anything yet" : `@${profile.username} hasn't posted yet`}
          </p>
        </div>
      ) : (
        <div>
          {posts.map((post) => (
            <PostCard key={post.post_id} post={post} />
          ))}
        </div>
      )}

      {/* Edit Profile Modal */}
      {isOwnProfile && (
        <EditProfileModal
          open={editOpen}
          onOpenChange={setEditOpen}
          profile={profile}
          onUpdate={handleProfileUpdate}
        />
      )}

      {/* Report Modal */}
      {!isOwnProfile && (
        <ReportModal
          open={reportOpen}
          onOpenChange={setReportOpen}
          targetType="user"
          targetId={profile.user_id}
          targetName={`@${profile.username}`}
        />
      )}
    </div>
  );
}
